import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Capacitor } from "@capacitor/core";
import { GENERIC_LINK_BASE_URL, resolveAppBranding } from "@/config/appBranding";
import i18n from "@/i18n";
import {
  consumeResetToken,
  type ResetTokenError,
} from "@/services/edgeFunctionWrappers/passwordResetWrapper";

const errorMessage = (reason?: ResetTokenError): string => {
  switch (reason) {
    case "expired":
      return i18n.t("passwordSetup.linkExpired", { ns: "appui" });
    case "already_used":
      return i18n.t("passwordSetup.linkUsed", { ns: "appui" });
    case "too_many_attempts":
      return i18n.t("passwordSetup.tooManyAttempts", { ns: "appui" });
    case "invalid_token":
      return i18n.t("passwordSetup.linkInvalid", { ns: "appui" });
    default:
      return i18n.t("passwordSetup.changeFailed", { ns: "appui" });
  }
};

export const usePasswordSetup = (
  appName: string = "StriFit",
  resetToken?: string | null
) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handlePasswordUpdate = async (password: string) => {
    setIsSubmitting(true);
    try {
      // Nouveau flux : le changement passe par le code du lien, sans session.
      if (resetToken) {
        const result = await consumeResetToken(resetToken, password);
        if (!result.success) {
          toast.error(errorMessage(result.reason));
          setIsSubmitting(false);
          return;
        }
        toast.success(i18n.t("passwordSetup.success", { ns: "appui" }));
        await handleLoginRedirect();
        return;
      }

      // Ancien flux (liens Supabase déjà envoyés) : nécessite la session recovery.
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData?.session) {
        toast.error(i18n.t("passwordSetup.sessionMissing", { ns: "appui" }));
        setIsSubmitting(false);
        return;
      }

      const { error } = await supabase.auth.updateUser({ password });

      if (error) {
        toast.error(error.message || i18n.t("passwordSetup.changeFailed", { ns: "appui" }));
        setIsSubmitting(false);
        return;
      }

      toast.success(i18n.t("passwordSetup.success", { ns: "appui" }));
      await handleLoginRedirect();
    } catch (err: any) {
      toast.error(err?.message || i18n.t("passwordSetup.unexpectedError", { ns: "appui" }));
      setIsSubmitting(false);
    }
  };

  const handleLoginRedirect = async () => {
    // Retour vers l'application qui a demandé la réinitialisation : chaque app
    // a son propre hôte, `core.stryvfit.app` étant revendiqué par toutes les
    // apps installées (il ouvrait parfois la mauvaise).
    const branding = resolveAppBranding(appName);
    // Dans l'app native, on reste sur place : simple retour à la connexion.
    if (Capacitor.isNativePlatform()) {
      window.location.href = "/login?success=1";
      return;
    }
    window.location.href =
      `${GENERIC_LINK_BASE_URL}/open-app/login?success=1&app=${branding.linkKey}` +
      `&appName=${encodeURIComponent(branding.key)}`;
  };

  return {
    isSubmitting,
    handlePasswordUpdate,
  };
};
